import { useId, type FC, type ReactNode } from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "@privacy/translations";
import { InfoIcon, TriangleAlertIcon } from "../shared/icons";
import { mergeSx } from "../shared/mergeSx";
import { RESPONSIBLE_FIELD_ID } from "../pages/RegistrationsGridPage/RegistrationDrawer/RegistrationResponsible";

const DecisionNoticeRoot: FC<{
  eyebrow: ReactNode;
  tone?: "info" | "warning";
  question: string;
  quote?: string | null;
  children?: ReactNode;
  sx?: SxProps<Theme>;
}> = ({ eyebrow, tone = "info", question, quote, children, sx }) => (
  <Box
    sx={mergeSx(
      theme => ({
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        px: "16px",
        pt: "16px",
        pb: children ? 0.5 : 3,
        overflowWrap: "anywhere",
        textWrap: "pretty",
      }),
      sx
    )}
  >
    <Typography
      sx={theme => ({
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 400,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: theme.palette.text.primary,
      })}
    >
      <Box component="span" sx={{ display: "inline-block", mr: 0.75, verticalAlign: "-2px" }}>
        {tone === "warning" ? (
          <TriangleAlertIcon size={14} color={theme => theme.palette.warning.main} />
        ) : (
          <InfoIcon size={14} color={theme => theme.palette.primary.main} />
        )}
      </Box>
      {eyebrow}
    </Typography>
    {quote && (
      <Box
        sx={theme => ({
          mt: 1.5,
          pl: 1.25,
          borderLeft: `2px solid ${theme.palette.grey["300"]}`,
          fontSize: theme.typography.pxToRem(14),
          fontStyle: "italic",
          color: "text.secondary",
        })}
      >
        {quote}
      </Box>
    )}
    <Typography sx={theme => ({ mt: 1.5, fontSize: theme.typography.pxToRem(16), fontWeight: 400 })}>
      {question}
    </Typography>
    {children && <Stack sx={{ mt: 2.5 }}>{children}</Stack>}
  </Box>
);

const DecisionNoticeAnswer: FC<{
  answer: "yes" | "no";
  consequence: string;
  onClick: () => void;
  isPending?: boolean;
  blockedReason?: string;
}> = ({ answer, consequence, onClick, isPending, blockedReason }) => {
  const { t } = useTranslation();
  const blockedReasonId = useId();

  return (
    <Box sx={{ pb: 2 }}>
      <ButtonBase
        disableRipple
        disabled={isPending}
        aria-disabled={blockedReason ? true : undefined}
        aria-describedby={blockedReason ? blockedReasonId : undefined}
        onClick={() => (blockedReason ? document.getElementById(RESPONSIBLE_FIELD_ID)?.focus() : onClick())}
        sx={theme => ({
          "display": "flex",
          "flexDirection": "column",
          "alignItems": "stretch",
          "textAlign": "left",
          "width": "100%",
          "p": "12px 16px",
          "gap": "4px",
          "borderRadius": 1,
          "border": `1px solid ${theme.palette.grey["400"]}`,
          "transition": "background-color 150ms ease-out",
          "@media (hover: hover)": { "&:hover:not(.Mui-disabled)": { bgcolor: theme.palette.action.hover } },
          "&.Mui-disabled": { pointerEvents: "auto", cursor: "progress", opacity: 0.6 },
          "&.Mui-focusVisible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        })}
      >
        <Typography
          component="span"
          sx={theme => ({ pt: "1px", fontWeight: 600, lineHeight: 1, fontSize: theme.typography.pxToRem(14) })}
        >
          {t(answer === "yes" ? "SharedProcessing.Decision.Yes" : "SharedProcessing.Decision.No")}
        </Typography>
        <Typography
          component="span"
          sx={theme => ({ fontSize: theme.typography.pxToRem(12), color: "text.secondary" })}
        >
          {consequence}
        </Typography>
        {blockedReason && (
          <Typography
            id={blockedReasonId}
            component="span"
            sx={theme => ({ fontSize: theme.typography.pxToRem(12), fontWeight: 600, color: "warning.dark" })}
          >
            {blockedReason}
          </Typography>
        )}
      </ButtonBase>
    </Box>
  );
};

const DecisionNoticeLine: FC<{ color?: string; children: ReactNode }> = ({ color = "text.secondary", children }) => (
  <Box sx={theme => ({ pb: 2, fontSize: theme.typography.pxToRem(14), color })}>{children}</Box>
);

const DecisionNoticeNoPermission = () => {
  const { t } = useTranslation();
  return <DecisionNoticeLine>{t("SharedProcessing.Decision.NoPermission")}</DecisionNoticeLine>;
};

const DecisionNoticeError: FC<{ message?: string }> = ({ message }) => {
  const { t } = useTranslation();
  return <DecisionNoticeLine color="error.main">{message ?? t("ConfirmDeleteDialog.GenericError")}</DecisionNoticeLine>;
};

export const DecisionNotice = Object.assign(DecisionNoticeRoot, {
  Answer: DecisionNoticeAnswer,
  Line: DecisionNoticeLine,
  NoPermission: DecisionNoticeNoPermission,
  Error: DecisionNoticeError,
});
